import { useRouter } from 'expo-router';
import { SourceName } from 'features/application/hooks/useApplicationTrackErrorEvents';
import {
  useStatusChangedFocusAnalytics,
  useStepStartedFocusAnalytics,
} from 'features/application/hooks/useStepStartedFocusAnalytics';
import DownloadMobileAppSuccessScreen from 'features/signup/DownloadMobileAppSuccessScreen/DownloadMobileAppSuccessScreen';
import useCustomerApplicationOrigination from 'features/signup/hooks/useCustomerApplicationOrigination';
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageRequireSource, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { savePendingRoute } from 'services/deeplinks/deeplinkManager';
import { FeatureFlagKey } from 'services/statsig/types';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { isMobileOS, isWeb } from 'shared/constants/platform';
import { STORAGE_KEYS } from 'shared/constants/storage';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useWhereToGo from 'shared/hooks/useWhereToGo/useWhereToGo';
import CelebrationScreen from 'shared/patterns/vxl/CelebrationScreen/CelebrationScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import theme from 'shared/styles/theme';
import { getItem } from 'shared/utils/asyncStorage';

const ApplicationApproved = ({
  isReactivation,
  isReapplication,
}: {
  isReactivation?: boolean;
  isReapplication?: boolean;
}) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'approved',
  });
  const insets = useSafeAreaInsets();
  const shouldShowDownloadAppScreen = isWeb && isMobileOS;

  const { handleRefreshToken, isLoginLoading, handleLogout } =
    useContext<AuthContextProps>(AuthContext);

  const { initiateWhereToGo } = useWhereToGo();

  const { isFeatureEnabled } = useStatsig();
  const isReactivationStandardOnboardingEnabled = isFeatureEnabled(
    FeatureFlagKey.REACTIVATION_STANDARD_ONBOARDING,
  );

  const { application } = useCustomerApplicationOrigination({
    sourceName: SourceName.APPLICATION_DECISION,
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: 'applicationAutoApproved',
    customAttributes: {
      ...(isReactivation && {
        reactivation_flow: 'true',
      }),
      ...(isReapplication && {
        reapplication_flow: 'true',
      }),
    },
  });

  useStepStartedFocusAnalytics({
    analyticsId: 'applicationAutoApproved',
    customAttributes: {
      ...(isReactivation && {
        reactivation_flow: 'true',
      }),
      ...(isReapplication && {
        reapplication_flow: 'true',
      }),
    },
  });

  useStatusChangedFocusAnalytics({
    analyticsId: 'statusChanged',
    customAttributes: {
      status: 'approved',
      ...(isReactivation && {
        reactivation_flow: 'true',
      }),
      ...(isReapplication && {
        reapplication_flow: 'true',
      }),
    },
  });

  const handleDoneButtonAction = async () => {
    const refreshToken = await getItem(STORAGE_KEYS.REFRESH_TOKEN);
    await handleRefreshToken({
      refreshToken: refreshToken ?? '',
      emailAddress: application?.email?.address ?? '',
    });

    const shouldGoToOnboarding =
      !isReactivation ||
      (isReactivation && isReactivationStandardOnboardingEnabled);

    if (shouldGoToOnboarding) {
      await savePendingRoute('/onboarding', {}, { isDeeplink: false });
      initiateWhereToGo({ requestPolicy: 'network-only' });
    }
    router.replace('/');
  };

  const handleBackButtonAction = useCallback(() => {
    void handleLogout();
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useBackHandler(handleBackButtonAction);

  return (
    <>
      <ScreenTransitionOverlay
        isLoading={isLoginLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={null}
      />

      {shouldShowDownloadAppScreen ? (
        <DownloadMobileAppSuccessScreen />
      ) : (
        <View
          style={[
            styles.container,
            {
              paddingBottom: insets.bottom,
            },
          ]}
        >
          <CelebrationScreen
            illustrationKey="CHECKING_APPROVAL_A_ANIMATED"
            illustrationAccessibilityLabel="Your bank account is ready celebration animation"
            bodyText={translatedText('description')}
            textColor={theme.color.text.inverse}
            button={{
              label: translatedText('done'),
              onDark: true,
              onPress: handleDoneButtonAction,
            }}
            backgroundTexture={
              // eslint-disable-next-line @typescript-eslint/no-require-imports
              require('assets/images/vxl/component/celebrationScreen/background-texture-purple.png') as ImageRequireSource
            }
          />
        </View>
      )}
    </>
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.color.interactive.primary,
  },
});

export default ApplicationApproved;
